import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Button, Group, NumberInput, Text, Divider,
    TextInput, Grid, Card, Title, ActionIcon, ScrollArea,
    Modal, Badge, Notification, Table, Loader,
    Select, Switch,
    MultiSelect
} from '@mantine/core';
import { IconTrash, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { doc, setDoc, getDoc, updateDoc, arrayUnion, Timestamp, deleteField } from 'firebase/firestore';
import UserGroupsSearch from '../../components/user_groups_search';
import { useFirestore } from '../../helpers/firebaseContext';
import { ConfigContext, MyUserDataContext } from '../../contexts';
import { DefaultPortfolio, GroupMapping, TradeAccountConfig } from '../../types';



interface NotificationState {
    visible: boolean;
    message: string;
    type: 'success' | 'error';
}

interface DeleteModalState {
    visible: boolean;
    type: 'mapping' | 'portfolio' | 'account';
    accountId?: string;
    portfolioIndex?: number;
}

export default function TradesSettings() {
    // state for mapping input
    const db = useFirestore();
    const userContext = useContext(MyUserDataContext);
    const configData = useContext(ConfigContext);
    const userEmail = userContext.user?.email || 'unknown';
    const [newAccountIdInput, setNewAccountIdInput] = useState<string>('');
    const [groupIdInput, setGroupIdInput] = useState<string>('');
    const [groupIdDisplayNameMap, setGroupIdDisplayNameMap] = useState<Record<string, string>>({});

    // state for default portfolios
    const [defaultAllocationAccountIdInput, setDefaultAllocationAccountIdInput] = useState<string>('');
    const [defaultAllocationPortfolioIdInput, setDefaultAllocationPortfolioIdInput] = useState<string>('');
    const [defaultAllocationSecurityIdInput, setDefaultAllocationSecurityIdInput] = useState<string>('');

    // Loading states
    const [loading, setLoading] = useState<boolean>(true);
    const [addingMapping, setAddingMapping] = useState<boolean>(false);
    const [addingAllocation, setAddingAllocation] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

    // Combined state for account configurations
    const [accountsConfig, setAccountsConfig] = useState<Record<string, TradeAccountConfig>>({});

    // State for Add Allocation Modal
    const [addAllocModalOpen, setAddAllocModalOpen] = useState<boolean>(false);
    const [addAllocAccountId, setAddAllocAccountId] = useState<string>('');

    // Display values for selected items
    // Re-purpose state for the modal inputs
    const [modalPortfolioId, setModalPortfolioId] = useState<string>('');
    const [modalSecurityId, setModalSecurityId] = useState<string>('');
    const [modalIsFund, setModalIsFund] = useState<boolean>(false); // New state for isFund

    // Notification state
    const [notification, setNotification] = useState<NotificationState>({
        visible: false,
        message: '',
        type: 'success'
    });

    // Delete confirmation modal
    const [deleteModal, setDeleteModal] = useState<DeleteModalState>({
        visible: false,
        type: 'account',
    });

    // Additional IDs per account
    const [burganKccIdInput, setBurganKccIdInput] = useState<string>('');
    const [burganBrokerIdInput, setBurganBrokerIdInput] = useState<string>('');
    const [burganStockcenterIdInput, setBurganStockcenterIdInput] = useState<string>('');
    const [genevaIdInput, setGenevaIdInput] = useState<string>('');
    const [genevaBrokerInput, setGenevaBrokerInput] = useState<string>('');
    const [portfolioTypeInput, setPortfolioTypeInput] = useState<string[]>([]);
    const [portfolioContractInput, setPortfolioContractInput] = useState<string[]>([]);
    const [allocateFromExcelInput, setAllocateFromExcelInput] = useState<boolean>(false);
    const [capitalizeExpensesInput, setCapitalizeExpensesInput] = useState<boolean>(false);

    // Load existing data from Firestore
    useEffect(() => {
        const fetchData = async () => {
            try {
                const docRef = doc(db, 'config', 'tradesMap');
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();

                    // New structure: accounts map
                    if (data.accounts) {
                        const newConfig: Record<string, TradeAccountConfig> = {};

                        Object.keys(data.accounts).forEach((accountId: string) => {
                            const acctData = data.accounts[accountId] || {};

                            // Ensure defaultPortfolios is an array, even if missing or null
                            const portfolios = Array.isArray(acctData.defaultPortfolios)
                                ? acctData.defaultPortfolios.map((p: any, idx: number) => ({
                                    ...p,
                                    id: `portfolio-${accountId}-${idx}`,
                                    isFund: p.isFund ?? false // Ensure isFund has a default value
                                }))
                                : [];

                            newConfig[accountId] = {
                                groupId: acctData.groupId || undefined, // Ensure undefined if missing
                                burgan_kcc_id: acctData.burgan_kcc_id || undefined,
                                burgan_broker_id: acctData.burgan_broker_id || undefined,
                                burgan_stockcenter_id: acctData.burgan_stockcenter_id || undefined,
                                geneva_id: acctData.geneva_id || undefined,
                                geneva_broker: acctData.geneva_broker || undefined,
                                portfolioTypeCode: acctData.portfolioTypeCode || undefined,
                                portfolioContract: acctData.portfolioContract || undefined,
                                allocateFromExcel: acctData.allocateFromExcel || undefined,
                                capitalizeExpenses: acctData.capitalizeExpenses || undefined,
                                defaultPortfolios: portfolios, // Use the processed array
                                addedBy: acctData.addedBy, // Preserve audit fields if they exist at account level
                                addedAt: acctData.addedAt,
                            };
                        });

                        setAccountsConfig(newConfig);
                    }
                }
            } catch (error) {
                console.error("Error fetching trades settings:", error);
                showNotification('Error loading settings', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [db]);

    // Notification helper
    const showNotification = (message: string, type: 'success' | 'error') => {
        setNotification({
            visible: true,
            message,
            type
        });

        // Auto-hide after 3 seconds
        setTimeout(() => {
            setNotification(prev => ({ ...prev, visible: false }));
        }, 3000);
    };

    // Validation: Check if group mapping already exists
    const isMappingDuplicate = (accountId: string, groupId: string): boolean => {
        const existingConfig = accountsConfig[accountId];
        return !!existingConfig && existingConfig.groupId === groupId;
    };

    // Validation: Check if portfolio allocation already exists or conflicts
    const isAllocationInvalid = (accountId: string, portfolioId: string, symbolId?: string): { valid: boolean; message: string } => {
        const accountConfig = accountsConfig[accountId];
        if (!accountConfig || !Array.isArray(accountConfig.defaultPortfolios)) {
            return { valid: true, message: '' }; // No existing allocations for this account
        }

        const allocations = accountConfig.defaultPortfolios;

        const exactDuplicate = allocations.some(
            p => !p.toDelete &&
                p.portfolioId === portfolioId &&
                (p.symbolId ?? '') === (symbolId ?? '')
        );

        if (exactDuplicate) {
            return {
                valid: false,
                message: 'This allocation already exists'
            };
        }

        // Check if this accountId + symbolId combination already exists with a different portfolioId
        const conflictWithDifferentPortfolio = allocations.some(
            p => !p.toDelete &&
                (p.symbolId ?? '') === (symbolId ?? '') &&
                p.portfolioId !== portfolioId
        );

        if (conflictWithDifferentPortfolio) {
            return {
                valid: false,
                message: 'This account and security combination already exists in a different portfolio'
            };
        }

        // If adding without a symbol, check if there's an entry with symbol for same accountId and portfolioId
        if (!symbolId) {
            const conflictWithSymbol = allocations.some(
                p => !p.toDelete &&
                    p.portfolioId === portfolioId &&
                    p.symbolId
            );

            if (conflictWithSymbol) {
                return {
                    valid: false,
                    message: 'Cannot add without security when an allocation with security exists'
                };
            }
        }

        // If adding with a symbol, check if there's an entry without symbol for same accountId and portfolioId
        if (symbolId) {
            const conflictWithoutSymbol = allocations.some(
                p => !p.toDelete &&
                    p.portfolioId === portfolioId &&
                    !p.symbolId
            );

            if (conflictWithoutSymbol) {
                return {
                    valid: false,
                    message: 'Cannot add with security when an allocation without security exists'
                };
            }
        }

        return { valid: true, message: '' };
    };

    // handle adding a new group mapping
    const handleAddMapping = async () => {
        if (newAccountIdInput === '' || !groupIdInput) {
            return;
        }

        // Check for duplicate
        if (isMappingDuplicate(newAccountIdInput, groupIdInput)) {
            showNotification('This group mapping already exists', 'error');
            return;
        }

        setAddingMapping(true);

        try {
            const newMapping: GroupMapping & { burgan_kcc_id?: string; burgan_broker_id?: string; burgan_stockcenter_id?: string; geneva_id?: string; geneva_broker?: string; portfolioTypeCode?: string[]; portfolioContract?: string[]; allocateFromExcel?: boolean } = {
                accountId: newAccountIdInput,
                groupId: groupIdInput,
                addedBy: userEmail,
                addedAt: Timestamp.now(),
            };

            if (burganKccIdInput) newMapping.burgan_kcc_id = burganKccIdInput;
            if (burganBrokerIdInput) newMapping.burgan_broker_id = burganBrokerIdInput;
            if (burganStockcenterIdInput) newMapping.burgan_stockcenter_id = burganStockcenterIdInput;
            if (genevaIdInput) newMapping.geneva_id = genevaIdInput;
            if (genevaBrokerInput) newMapping.geneva_broker = genevaBrokerInput;
            if (portfolioTypeInput) newMapping.portfolioTypeCode = portfolioTypeInput;
            if (portfolioContractInput.length > 0) newMapping.portfolioContract = portfolioContractInput;
            if (allocateFromExcelInput) newMapping.allocateFromExcel = true;

            // Update Firestore - new nested path under accounts
            const docRef = doc(db, 'config', 'tradesMap');
            const payload: Record<string, any> = {
                accounts: {
                    [newAccountIdInput]: {
                        groupId: groupIdInput,
                    },
                },
            };
            const accountData = payload.accounts[newAccountIdInput];
            if (burganKccIdInput) accountData.burgan_kcc_id = burganKccIdInput;
            if (burganBrokerIdInput) accountData.burgan_broker_id = burganBrokerIdInput;
            if (burganStockcenterIdInput) accountData.burgan_stockcenter_id = burganStockcenterIdInput;
            if (genevaIdInput) accountData.geneva_id = genevaIdInput;
            if (genevaBrokerInput) accountData.geneva_broker = genevaBrokerInput;
            if (portfolioTypeInput) accountData.portfolioTypeCode = portfolioTypeInput;
            /* both always written so they can also be CLEARED/turned off by re-adding
               the mapping (the write is merge-based, omitting them would keep old values) */
            accountData.portfolioContract = portfolioContractInput;
            accountData.allocateFromExcel = allocateFromExcelInput;
            accountData.capitalizeExpenses = capitalizeExpensesInput;

            await setDoc(docRef, payload, { merge: true });

            // Add to local state with ID
            const mappingWithId = {
                ...newMapping,
                id: `mapping-new-${Date.now()}`
            };

            setAccountsConfig(prev => ({
                ...prev,
                [newAccountIdInput]: {
                    ...prev[newAccountIdInput],
                    groupId: groupIdInput,
                    capitalizeExpenses: capitalizeExpensesInput || undefined,
                    toDelete: false,
                    id: `mapping-${newAccountIdInput}`,
                }
            }));
            showNotification('Group mapping added successfully', 'success');

            // reset inputs
            setNewAccountIdInput('');
            setGroupIdInput('');
            setBurganKccIdInput('');
            setBurganBrokerIdInput('');
            setBurganStockcenterIdInput('');
            setGenevaIdInput('');
            setGenevaBrokerInput('');
            setPortfolioTypeInput([]);
            setPortfolioContractInput([]);
            setAllocateFromExcelInput(false);
            setCapitalizeExpensesInput(false);
        } catch (error) {
            console.error("Error adding group mapping:", error);
            showNotification('Error adding group mapping', 'error');
        } finally {
            setAddingMapping(false);
        }
    };

    // handle department selection, only keep first
    const handleDepartmentPicked = (ids: string[]) => {
        if (ids.length > 0) {
            setGroupIdInput(ids[0]);
        } else {
            setGroupIdInput('');
        }
    };

    // handlers for portfolios
    const handleAddDefaultAllocation = async () => {
        // Use modal states: addAllocAccountId, modalPortfolioId, modalSecurityId, modalIsFund
        if (!addAllocAccountId || !modalPortfolioId) {
            // Maybe show an error specific to the modal?
            console.error("Account ID or Portfolio ID missing in Add Allocation Modal");
            showNotification('Account ID and Portfolio ID are required', 'error'); // User-facing error
            return;
        }

        // Validate allocation within the specific account
        const validationResult = isAllocationInvalid(
            addAllocAccountId, // Use state variable holding target account
            modalPortfolioId,
            modalSecurityId
        );

        if (!validationResult.valid) {
            showNotification(validationResult.message, 'error');
            return;
        }

        setAddingAllocation(true);

        try {
            const newPortfolio: DefaultPortfolio = {
                accountId: addAllocAccountId, // Use state variable
                portfolioId: modalPortfolioId,
                isFund: modalIsFund,
                addedBy: userEmail,
                addedAt: Timestamp.now()
            };

            if (modalSecurityId) {
                newPortfolio.symbolId = modalSecurityId;
            }

            // Update Firestore - nested path under accounts
            const docRef = doc(db, 'config', 'tradesMap');
            const allocationPayload = {
                accounts: {
                    [addAllocAccountId]: {
                        defaultPortfolios: arrayUnion(newPortfolio),
                    },
                },
            };
            await setDoc(docRef, allocationPayload, { merge: true });

            // Add to local state with ID
            const portfolioWithId = {
                ...newPortfolio,
                id: `portfolio-${addAllocAccountId}-${Date.now()}` // Use state variable
            };

            setAccountsConfig(prev => {
                const accountConf = prev[addAllocAccountId]; // Use state variable
                const existingPortfolios = accountConf?.defaultPortfolios || [];
                const updatedAccount = {
                    ...(accountConf || { groupId: undefined as any, defaultPortfolios: [] }), // Ensure account exists
                    defaultPortfolios: [...existingPortfolios, portfolioWithId],
                };
                return {
                    ...prev,
                    [addAllocAccountId]: updatedAccount,
                };
            });

            showNotification('Default allocation added successfully', 'success');

            // Close modal and reset modal-specific states/inputs
            closeAddAllocationModal();

        } catch (error) {
            console.error("Error adding default allocation:", error);
            showNotification('Error adding default allocation', 'error');
        } finally {
            setAddingAllocation(false);
        }
    };

    // Delete handlers
    const openDeleteModal = (type: 'account' | 'portfolio', accountId: string, portfolioIndex?: number) => {
        setDeleteModal({
            visible: true,
            type,
            accountId,
            portfolioIndex: portfolioIndex
        });
    };

    const closeDeleteModal = () => {
        setDeleteModal({
            visible: false,
            type: 'account',
        });
    };

    const confirmDelete = async () => {
        const { type, accountId, portfolioIndex } = deleteModal;

        if (!accountId) return;

        setDeleteLoading(true);

        try {
            const docRef = doc(db, 'config', 'tradesMap');
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();

                if (type === 'account') {
                    // Remove directly
                    setAccountsConfig(prev => {
                        const { [accountId]: _, ...rest } = prev;
                        return rest;
                    });

                    // Remove the entire account key from Firestore map
                    const accountPath = `accounts.${accountId}`;
                    await updateDoc(docRef, {
                        [accountPath]: deleteField(),
                    });
                } else if (type === 'portfolio' && portfolioIndex !== undefined) {
                    // Delete a specific portfolio from an account
                    const currentConfig = accountsConfig[accountId];
                    if (!currentConfig || !currentConfig.defaultPortfolios) return; // Safety check

                    const portfolioToDelete = currentConfig.defaultPortfolios[portfolioIndex];
                    if (!portfolioToDelete) return; // Safety check

                    // Update local state
                    setAccountsConfig(prev => ({
                        ...prev,
                        [accountId]: {
                            ...prev[accountId],
                            defaultPortfolios: prev[accountId].defaultPortfolios.filter((_, idx) => idx !== portfolioIndex),
                        },
                    }));

                    // Fetch existing portfolios from Firestore to correctly update the array
                    const acctData = data.accounts && data.accounts[accountId] ? data.accounts[accountId] : { defaultPortfolios: [] };
                    const existingPortfolios: DefaultPortfolio[] = Array.isArray(acctData.defaultPortfolios) ? acctData.defaultPortfolios : [];

                    // Filter out the specific portfolio to delete based on its properties
                    const updatedFirestorePortfolios = existingPortfolios.filter(
                        (p: DefaultPortfolio) =>
                            !(p.portfolioId === portfolioToDelete.portfolioId &&
                                (p.symbolId ?? '') === (portfolioToDelete.symbolId ?? ''))
                    );

                    await updateDoc(docRef, {
                        [`accounts.${accountId}.defaultPortfolios`]: updatedFirestorePortfolios,
                    });
                }

                showNotification('Item deleted successfully', 'success');
            }
        } catch (error) {
            console.error("Error deleting item:", error);
            showNotification('Error deleting item', 'error');
        } finally {
            setDeleteLoading(false);
            closeDeleteModal();
        }
    };

    // --- Modal Control --- 
    const openAddAllocationModal = (accountId: string) => {
        setAddAllocAccountId(accountId); // Store the target account
        setModalPortfolioId(''); // Reset modal inputs
        setModalSecurityId('');
        setModalIsFund(false);
        setAddAllocModalOpen(true);
    };

    const closeAddAllocationModal = () => {
        setAddAllocModalOpen(false);
        setAddAllocAccountId(''); // Clear the target account
        setModalIsFund(false); // Reset isFund
    };
    // -------------------

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <Loader size="lg" />
            </div>
        );
    }

    console.log('Accounts Config:', accountsConfig);

    return (
        <>
            <Grid columns={6} gap="xs">
                {/* Group Mapping - 2 cells */}
                <Grid.Col span={2.97}>
                    <Card withBorder radius="sm" p="xs">
                        <Group mb="xs">
                            <Title order={6}>Group Mapping</Title>
                        </Group>
                        <Divider mb="xs" />
                        <Grid columns={8} gap="xs">
                            <Grid.Col span={3}>
                                <TextInput
                                    label="Account ID"
                                    placeholder="Enter account id"
                                    value={newAccountIdInput}
                                    onChange={(e) => setNewAccountIdInput(e.target.value.trim())}
                                    disabled={addingMapping}
                                />
                            </Grid.Col>
                            <Grid.Col span={5}>
                                <UserGroupsSearch
                                    values={groupIdInput ? [groupIdInput] : []}
                                    label="Department Picker"
                                    onItemSubmit={handleDepartmentPicked}
                                    onDataMapRetrieved={(data: Record<string, string>) => setGroupIdDisplayNameMap(data)}
                                    apiURl="/groups/"
                                    disabled={addingMapping}
                                />
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <TextInput
                                    label="Geneva ID"
                                    placeholder="(optional)"
                                    value={genevaIdInput}
                                    onChange={(e) => setGenevaIdInput(e.target.value)}
                                    disabled={addingMapping}
                                />
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <TextInput
                                    label="Geneva Broker"
                                    placeholder="(optional)"
                                    value={genevaBrokerInput}
                                    onChange={(e) => setGenevaBrokerInput(e.target.value.trim())}
                                    disabled={addingMapping}
                                />
                            </Grid.Col>
                            <Grid.Col span={5}>
                                <MultiSelect
                                    label="Portfolio Type"
                                    placeholder="(optional)"
                                    disabled={addingMapping}
                                    value={portfolioTypeInput}
                                    onClear={() => setPortfolioTypeInput([])}
                                    onChange={(value) => setPortfolioTypeInput(value ?? [])}
                                    data={configData.tradesMap?.portfoliosType ?? ['DPM', 'NonDPM', 'Custody', 'KFHC Fund']}
                                />
                            </Grid.Col>
                            <Grid.Col span={5}>
                                <MultiSelect
                                    label="Portfolio Contract"
                                    placeholder="(optional)"
                                    disabled={addingMapping}
                                    value={portfolioContractInput}
                                    onClear={() => setPortfolioContractInput([])}
                                    onChange={(value) => setPortfolioContractInput(value ?? [])}
                                    data={['DPM_GLOBAL', 'CUSTODY_GLOBAL']}
                                />
                            </Grid.Col>
                            <Grid.Col span={2}>
                                <TextInput
                                    label="Burgan Broker ID"
                                    placeholder="(optional)"
                                    value={burganBrokerIdInput}
                                    onChange={(e) => setBurganBrokerIdInput(e.target.value)}
                                    disabled={addingMapping}
                                />
                            </Grid.Col>
                            <Grid.Col span={3}>
                                <TextInput
                                    label="Burgan KCC ID"
                                    placeholder="(optional)"
                                    value={burganKccIdInput}
                                    onChange={(e) => setBurganKccIdInput(e.target.value)}
                                    disabled={addingMapping}
                                />
                            </Grid.Col>

                            <Grid.Col span={3}>
                                <TextInput
                                    label="StockCenter ID"
                                    placeholder="(optional)"
                                    value={burganStockcenterIdInput}
                                    onChange={(e) => setBurganStockcenterIdInput(e.target.value)}
                                    disabled={addingMapping}
                                />
                            </Grid.Col>

                            <Grid.Col span={4}>
                                <Switch
                                    label="Auto-allocate from Global DPM excel"
                                    description="Settled trades get allocated from uploaded excel rows"
                                    mt="md"
                                    checked={allocateFromExcelInput}
                                    onChange={(e) => setAllocateFromExcelInput(e.currentTarget.checked)}
                                    disabled={addingMapping}
                                />
                            </Grid.Col>

                            <Grid.Col span={4}>
                                <Switch
                                    label="Capitalize expenses (Geneva)"
                                    description="New trades send fees as capitalized TradeExpenses; existing trades are unchanged"
                                    mt="md"
                                    checked={capitalizeExpensesInput}
                                    onChange={(e) => setCapitalizeExpensesInput(e.currentTarget.checked)}
                                    disabled={addingMapping}
                                />
                            </Grid.Col>

                        </Grid>

                        <Button
                            size="xs"
                            variant="light"
                            color="blue"
                            fullWidth
                            mt={10}
                            disabled={!newAccountIdInput || !groupIdInput || addingMapping}
                            loading={addingMapping}
                            onClick={handleAddMapping}
                        >
                            Add Mapping
                        </Button>

                        {/* Display Group Mappings -> Now Combined Account Config List */}
                        <Box mt="md">
                            {Object.keys(accountsConfig).length > 0 ? (
                                Object.entries(accountsConfig).map(([accountId, config]) => (
                                    <Card key={accountId} withBorder radius="sm" p="xs" mb="sm">
                                        <Group >
                                            <Title order={6}>Account: {accountId}</Title>
                                            <ActionIcon
                                                color="red"
                                                variant="subtle"
                                                size="xs"
                                                onClick={() => openDeleteModal('account', accountId)}
                                                style={{ marginLeft: 'auto' }} // Push delete icon to the right
                                            >
                                                <IconTrash size={14} />
                                            </ActionIcon>
                                        </Group>
                                        <Divider my="xs" />
                                        <Grid columns={6} gap="xs">
                                            <Grid.Col span={2}><Text size="xs">Group ID:</Text><Text fw={500} size="sm">{config.groupId ? groupIdDisplayNameMap[config.groupId] ? groupIdDisplayNameMap[config.groupId] : config.groupId : '—'}</Text></Grid.Col>
                                            <Grid.Col span={2}><Text size="xs">Burgan KCC ID:</Text><Text fw={500} size="sm">{config.burgan_kcc_id || '—'}</Text></Grid.Col>
                                            <Grid.Col span={2}><Text size="xs">Burgan Broker ID:</Text><Text fw={500} size="sm">{config.burgan_broker_id || '—'}</Text></Grid.Col>
                                            <Grid.Col span={2}><Text size="xs">StockCenter ID:</Text><Text fw={500} size="sm">{config.burgan_stockcenter_id || '—'}</Text></Grid.Col>
                                            <Grid.Col span={2}><Text size="xs">Geneva ID:</Text><Text fw={500} size="sm">{config.geneva_id || '—'}</Text></Grid.Col>
                                            <Grid.Col span={2}><Text size="xs">Geneva Broker:</Text><Text fw={500} size="sm">{config.geneva_broker || '—'}</Text></Grid.Col>
                                            {/* handle that config.portfolioTypeCode could be a string or an array of strings; if it is a string, then show it or add it to an array by itself */}
                                            <Grid.Col span={2}><Text size="xs">Portfolio Type:</Text><Text fw={500} size="sm">{Array.isArray(config.portfolioTypeCode) ? (config.portfolioTypeCode.length > 0 ? config.portfolioTypeCode.join(', ') : '—') : (config.portfolioTypeCode || '—')}</Text></Grid.Col>
                                            <Grid.Col span={2}><Text size="xs">Portfolio Contract:</Text><Text fw={500} size="sm">{config.portfolioContract && config.portfolioContract.length > 0 ? config.portfolioContract.join(', ') : '—'}</Text></Grid.Col>
                                            <Grid.Col span={2}><Text size="xs">Excel Auto-Allocation:</Text><Text fw={500} size="sm">{config.allocateFromExcel ? 'On' : 'Off'}</Text></Grid.Col>
                                            <Grid.Col span={2}><Text size="xs">Capitalize Expenses:</Text><Text fw={500} size="sm">{config.capitalizeExpenses ? 'Yes' : 'No'}</Text></Grid.Col>
                                        </Grid>

                                        <Title order={6} mt="md">Default Allocations</Title>
                                        <Divider my="xs" />
                                        {/* Add Allocation Form/Button (Placeholder) */}
                                        <Button
                                            size="xs"
                                            variant="light"
                                            mb="xs"
                                            onClick={() => openAddAllocationModal(accountId)}
                                        >
                                            Add Allocation for {accountId}
                                        </Button>

                                        {config.defaultPortfolios && config.defaultPortfolios.length > 0 ? (
                                            <ScrollArea h={150}>
                                                <Table striped highlightOnHover verticalSpacing="xs">
                                                    <Table.Thead>
                                                        <Table.Tr>
                                                            <Table.Th>Portfolio ID</Table.Th>
                                                            <Table.Th>Security ID</Table.Th>
                                                            <Table.Th>Is Fund</Table.Th>
                                                            <Table.Th style={{ width: 40 }}></Table.Th>
                                                        </Table.Tr>
                                                    </Table.Thead>
                                                    <Table.Tbody>
                                                        {config.defaultPortfolios.map((portfolio, index) => (
                                                            <Table.Tr key={`alloc-${index}`}>
                                                                <Table.Td>{portfolio.portfolioId}</Table.Td>
                                                                <Table.Td>{portfolio.symbolId || '—'}</Table.Td>
                                                                <Table.Td>
                                                                    <Badge
                                                                        color={portfolio.isFund ? 'green' : 'gray'}
                                                                        variant="light"
                                                                        size="sm"
                                                                    >
                                                                        {portfolio.isFund ? 'Yes' : 'No'}
                                                                    </Badge>
                                                                </Table.Td>
                                                                <Table.Td>
                                                                    <ActionIcon
                                                                        color="red"
                                                                        variant="subtle"
                                                                        size="xs"
                                                                        onClick={() => openDeleteModal('portfolio', accountId, index)}
                                                                    >
                                                                        <IconTrash size={14} />
                                                                    </ActionIcon>
                                                                </Table.Td>
                                                            </Table.Tr>
                                                        ))}
                                                    </Table.Tbody>
                                                </Table>
                                            </ScrollArea>
                                        ) : (
                                            <Text size="sm" c="dimmed">No default allocations set.</Text>
                                        )}
                                    </Card>
                                ))
                            ) : (
                                <Text mt="md" c="dimmed" ta="center">No accounts configured yet.</Text>
                            )}
                        </Box>
                    </Card>
                </Grid.Col>

                <Divider orientation="vertical" />

                {/* Removed the separate Default Allocation Card */}
                {/* <Grid.Col span={3}> ... </Grid.Col> */}
            </Grid>

            {/* Delete Confirmation Modal */}
            <Modal
                opened={deleteModal.visible}
                onClose={closeDeleteModal}
                title="Confirm Deletion"
                size="sm"
            >
                <Text size="sm" mb="md">
                    {deleteModal.type === 'account' && `Are you sure you want to delete all configuration for account ${deleteModal.accountId}?`}
                    {deleteModal.type === 'portfolio' && `Are you sure you want to delete this default allocation for account ${deleteModal.accountId}?`}
                </Text>
                <Group style={{ justifyContent: 'flex-end' }} >
                    <Button variant="outline" onClick={closeDeleteModal} disabled={deleteLoading}>
                        Cancel
                    </Button>
                    <Button color="red" onClick={confirmDelete} loading={deleteLoading}>
                        Delete
                    </Button>
                </Group>
            </Modal>

            {/* Add Allocation Modal */}
            <Modal
                opened={addAllocModalOpen}
                onClose={closeAddAllocationModal}
                title={`Add Default Allocation for Account: ${addAllocAccountId}`}
                size="lg" // Make it wider for the inputs
            >
                <Grid columns={6} gap="xs">
                    {/* Account ID is fixed based on addAllocAccountId */}
                    <Grid.Col span={6} mb="md">
                        <Text>Account ID: <Text span fw={700}>{addAllocAccountId}</Text></Text>
                    </Grid.Col>
                    <Grid.Col span={3}>
                        <TextInput
                            label="Portfolio ID"
                            placeholder="Enter portfolio ID"
                            value={modalPortfolioId} // Use modal state
                            onChange={(event) => setModalPortfolioId(event.currentTarget.value)} // Correct handler
                            disabled={addingAllocation}
                            required // Make it visually required
                        />
                    </Grid.Col>
                    <Grid.Col span={3}>
                        <TextInput
                            label="Security ID"
                            value={modalSecurityId} // Use modal state
                            onChange={(event) => setModalSecurityId(event.currentTarget.value)} // Correct handler
                            placeholder="(optional)"
                            disabled={addingAllocation}
                        />
                    </Grid.Col>
                    <Grid.Col span={6}>
                        <Switch
                            label="Is Fund"
                            description="Toggle if this allocation is a fund to add fund structure"
                            checked={modalIsFund}
                            onChange={(event) => setModalIsFund(event.currentTarget.checked)}
                            disabled={addingAllocation}
                            size="md"
                            mt="sm"
                        />
                    </Grid.Col>
                </Grid>
                <Group style={{ justifyContent: 'flex-end' }} mt="md">
                    <Button variant="outline" onClick={closeAddAllocationModal} disabled={addingAllocation}>
                        Cancel
                    </Button>
                    <Button
                        color="blue"
                        onClick={handleAddDefaultAllocation}
                        loading={addingAllocation}
                        disabled={!modalPortfolioId} // Disable if portfolio is not selected
                    >
                        Add Allocation
                    </Button>
                </Group>
            </Modal>

            {/* Notifications */}
            {notification.visible && (
                <Notification
                    color={notification.type === 'success' ? 'green' : 'red'}
                    title={notification.type === 'success' ? 'Success' : 'Error'}
                    icon={notification.type === 'success' ? <IconCheck /> : <IconAlertCircle />}
                    onClose={() => setNotification(prev => ({ ...prev, visible: false }))}
                    style={{
                        position: 'fixed',
                        bottom: '20px',
                        right: '20px',
                        zIndex: 1000
                    }}
                >
                    {notification.message}
                </Notification>
            )}
        </>
    );
}
